import { Extensions } from '@tiptap/core'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import TableRow from '@tiptap/extension-table-row'
import Underline from '@tiptap/extension-underline'
import StarterKit from '@tiptap/starter-kit'
import { TableImproved } from 'mui-tiptap'

import { isAllowedLinkUrl, LINK_BUTTON_CLASS, normalizeLinkInput } from './linkPolicy'

export type RichTextFeature = 'table' | 'link'

// Every feature a stored text may contain. Read-only views render all of them,
// so content written in a richer editor never gets dropped on display.
export const ALL_RICH_TEXT_FEATURES: RichTextFeature[] = ['table', 'link']

// Link mark whose class attribute is limited to the button variant, so stored
// HTML cannot smuggle arbitrary classes into the page.
const RichTextLink = Link.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      class: {
        default: null,
        parseHTML: element =>
          element.classList.contains(LINK_BUTTON_CLASS) ? LINK_BUTTON_CLASS : null
      }
    }
  }
})

type BuildRichTextExtensionsOptions = {
  features?: RichTextFeature[]
  placeholder?: string
  editable?: boolean
}

export const buildRichTextExtensions = ({
  features = [],
  placeholder,
  editable = true
}: BuildRichTextExtensionsOptions = {}): Extensions => [
  StarterKit,
  Underline,
  ...(placeholder ? [Placeholder.configure({ placeholder })] : []),
  ...(features.includes('table')
    ? [TableImproved.configure({ resizable: true }), TableRow, TableHeader, TableCell]
    : []),
  ...(features.includes('link')
    ? [
        RichTextLink.configure({
          // In the editor a click places the cursor; in read-only views the
          // browser follows the link (target=_blank).
          openOnClick: false,
          autolink: editable,
          linkOnPaste: editable,
          defaultProtocol: 'https',
          // Autolink and paste hand over the typed text ("www.kanzleihohner.de"), not
          // the final href, so a missing scheme is completed before checking
          isAllowedUri: url => isAllowedLinkUrl(normalizeLinkInput(url)),
          shouldAutoLink: url => isAllowedLinkUrl(normalizeLinkInput(url))
        })
      ]
    : [])
]
